/**
 * Los dos interruptores de la regla de pago por adelantado de la empresa, cada uno con sus días.
 *
 * - "Pago por adelantado" (`pagoAdelantado` + `diasPrepago`): por defecto apagado.
 * - "Factor riesgo" (`factorRiesgoActivo` + `diasAntelacionPagoFactorRiesgo`): por defecto activo, y
 *   el campo ausente cuenta como activo, igual que en el backend.
 *
 * Al menos uno de los dos tiene que quedar activo: el backend responde 400 si no.
 */
export const INTERRUPTOR_PAGO_ADELANTADO = 'pagoAdelantado';
export const INTERRUPTOR_FACTOR_RIESGO = 'factorRiesgoActivo';

export const MAXIMO_DIGITOS_DIAS_PREPAGO = 2;

const PATRON_ENTERO_NO_NEGATIVO = /^\d+$/;
const PATRON_ENTRADA_DIAS = /^\d*$/;

const LECTORES_ESTADO_INTERRUPTOR = {
    [INTERRUPTOR_PAGO_ADELANTADO]: (empresa) => empresa.pagoAdelantado === true,
    [INTERRUPTOR_FACTOR_RIESGO]: (empresa) => empresa.factorRiesgoActivo !== false,
};

const CAMPOS_DIAS_PREPAGO = [
    { campo: 'diasPrepago', interruptor: INTERRUPTOR_PAGO_ADELANTADO, etiqueta: 'Pago por adelantado' },
    { campo: 'diasAntelacionPagoFactorRiesgo', interruptor: INTERRUPTOR_FACTOR_RIESGO, etiqueta: 'Factor riesgo' },
];

export const estaInterruptorActivo = (empresa = {}, interruptor) =>
    LECTORES_ESTADO_INTERRUPTOR[interruptor](empresa);

/**
 * El interruptor que no se puede apagar por ser el único activo, o `null` si no hay ninguno así.
 * Con los dos apagados (datos antiguos) no se bloquea ninguno, para que se pueda encender uno.
 */
export const interruptorBloqueado = (empresa = {}) => {
    const interruptoresActivos = Object.keys(LECTORES_ESTADO_INTERRUPTOR)
        .filter((interruptor) => estaInterruptorActivo(empresa, interruptor));
    const quedaUnoSolo = interruptoresActivos.length === 1;
    return quedaUnoSolo ? interruptoresActivos[0] : null;
};

/** Lo que se teclea en un input de días: solo dígitos y como mucho los permitidos. */
export const esEntradaDiasValida = (textoTecleado) =>
    PATRON_ENTRADA_DIAS.test(textoTecleado) && textoTecleado.length <= MAXIMO_DIGITOS_DIAS_PREPAGO;

/**
 * Días como entero >= 0, o `null` si el valor no lo es. No corrige nada: `'2.5'` o `'-1'` son
 * inválidos, no 2 ni 1.
 */
export const normalizarDias = (valor) => {
    if (typeof valor === 'number') {
        const esEnteroNoNegativo = Number.isInteger(valor) && valor >= 0;
        return esEnteroNoNegativo ? valor : null;
    }
    if (typeof valor !== 'string') return null;
    if (!PATRON_ENTERO_NO_NEGATIVO.test(valor)) return null;
    return Number(valor);
};

const mensajeDiasInvalidos = (etiquetas) =>
    `Los días de ${etiquetas.join(' y de ')} deben ser un número entero igual o mayor que 0`;

/**
 * Prepara los días de los dos interruptores para el guardado de la empresa.
 *
 * - Valor válido: viaja como número.
 * - Sin valor en la empresa (`undefined`/`null`): la clave se omite y el backend conserva lo guardado.
 * - Valor inválido con su interruptor activo: error, no se guarda.
 * - Valor inválido con su interruptor apagado: la clave se omite. Su input está deshabilitado, así
 *   que exigir corregirlo dejaría al usuario sin salida, y no se inventa un valor.
 *
 * Devuelve la empresa a guardar y el mensaje de error (`null` si no lo hay).
 */
export const prepararDiasPrepagoParaGuardar = (empresa = {}) => {
    const empresaParaGuardar = { ...empresa };
    const etiquetasInvalidas = [];

    CAMPOS_DIAS_PREPAGO.forEach(({ campo, interruptor, etiqueta }) => {
        const valorActual = empresa[campo];
        const sinValor = valorActual === undefined || valorActual === null;
        delete empresaParaGuardar[campo];
        if (sinValor) return;

        const dias = normalizarDias(valorActual);
        if (dias !== null) {
            empresaParaGuardar[campo] = dias;
            return;
        }
        if (estaInterruptorActivo(empresa, interruptor)) etiquetasInvalidas.push(etiqueta);
    });

    const mensajeError = etiquetasInvalidas.length > 0 ? mensajeDiasInvalidos(etiquetasInvalidas) : null;
    return { empresaParaGuardar, mensajeError };
};
